import React, { useState } from 'react';
import './LeadCaptureForm.css';

/**
 * LeadCaptureForm Component
 *
 * Form for capturing lead contact information after assessment completion.
 * Includes validation and submission handling.
 */
const LeadCaptureForm = ({
  onSubmit,
  isSubmitting = false,
  previewScore = null,
  className = ''
}) => {
  const [formData, setFormData] = useState({
    full_name: '',
    business_name: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'full_name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';

      case 'business_name':
        if (!value.trim()) return 'Business name is required';
        if (value.trim().length < 2) return 'Business name must be at least 2 characters';
        return '';

      case 'email':
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return '';

      case 'phone':
        // Phone is optional, but if provided, validate format
        if (value.trim()) {
          const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
          if (!phoneRegex.test(value)) return 'Please enter a valid phone number';
          if (value.replace(/\D/g, '').length < 10) return 'Phone number must be at least 10 digits';
        }
        return '';

      default:
        return '';
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  // Handle blur (mark field as touched)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit form
    onSubmit(formData);
  };

  return (
    <div className={`lead-capture-form-container ${className}`}>
      {/* Preview Score (if provided) */}
      {previewScore !== null && (
        <div className="lead-preview-score">
          <div className="preview-score-blur">
            <div className="preview-score-number">{Math.round(previewScore)}%</div>
            <div className="preview-score-label">Your Exit Readiness Score</div>
          </div>
          <div className="preview-score-unlock">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2" />
            </svg>
            <span>Enter your information to unlock full results</span>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="lead-capture-form">
        <div className="form-header">
          <h3>Get Your Full Results</h3>
          <p>Enter your information to receive your complete Exit Readiness Assessment, personalized recommendations, and a downloadable PDF report.</p>
        </div>

        <div className="form-fields">
          {/* Full Name */}
          <div className="form-field">
            <label htmlFor="full_name" className="form-label">
              Full Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${errors.full_name && touched.full_name ? 'error' : ''}`}
              placeholder="John Smith"
              disabled={isSubmitting}
              autoComplete="name"
            />
            {errors.full_name && touched.full_name && (
              <div className="form-error">{errors.full_name}</div>
            )}
          </div>

          {/* Business Name */}
          <div className="form-field">
            <label htmlFor="business_name" className="form-label">
              Business Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="business_name"
              name="business_name"
              value={formData.business_name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${errors.business_name && touched.business_name ? 'error' : ''}`}
              placeholder="ABC Manufacturing Co."
              disabled={isSubmitting}
              autoComplete="organization"
            />
            {errors.business_name && touched.business_name && (
              <div className="form-error">{errors.business_name}</div>
            )}
          </div>

          {/* Email */}
          <div className="form-field">
            <label htmlFor="email" className="form-label">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${errors.email && touched.email ? 'error' : ''}`}
              placeholder="john@company.com"
              disabled={isSubmitting}
              autoComplete="email"
            />
            {errors.email && touched.email && (
              <div className="form-error">{errors.email}</div>
            )}
          </div>

          {/* Phone (Optional) */}
          <div className="form-field">
            <label htmlFor="phone" className="form-label">
              Phone Number <span className="optional">(Optional)</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-input ${errors.phone && touched.phone ? 'error' : ''}`}
              placeholder="(555) 123-4567"
              disabled={isSubmitting}
              autoComplete="tel"
            />
            {errors.phone && touched.phone && (
              <div className="form-error">{errors.phone}</div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="form-submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="btn-spinner" />
              Submitting...
            </>
          ) : (
            'See Full Results'
          )}
        </button>

        {/* Privacy Notice */}
        <div className="form-privacy-notice">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Your information is secure and will never be shared with third parties.</span>
        </div>
      </form>

      {/* Copyright Footer */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
        <div className="assessment-footer">
          © 2025 Dominion Business Advisors LLC. All Rights Reserved.
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureForm;
